import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { detailExpandAnimation } from '@app/api/core/utils/helper/offerteantraege-helper';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { ReminderComponent } from '@app/shared/components/reminder/reminder.component';
import { ThrottleEventDirective } from '@app/shared/directives/throttle-event.directive';
import { FormatAdressPipe } from '@app/shared/pipes/formatadress.pipe';
import { FormatDatePipe } from '@app/shared/pipes/formatdate.pipe';
import { MessageType } from '@app/shared/services/ui/message/message';
import { BasicPolizzeListComponent } from '@app/views/vertriebscockpit/offerteantraege/basic-polizze-list/basic-polizze-list.component';
import { getDocumentStatusText, getSimpleVollmachtStatus, getVollmachtStatusText, maklerDocumentExists, MaklerDocumentLayoutConfig, SIMPLE_VOLLMACHT_STATUS_CONFIG } from '@app/views/vertriebscockpit/offerteantraege/makler/offerteantraege-makler.helper';
import { RejectAntragDialogComponent } from '@app/views/vertriebscockpit/offerteantraege/offene-makler-antraege/reject-antrag-dialog/reject-antrag-dialog.component';
import { AntragChangeType, MaklerDocumentType, MaklerPolizzeDetails, PolizzeResult } from '@o30/entities';
import { EMPTY } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

// TODO alternating colors for table rows (& detail rows)
/**
 * "Anträge zur Prüfung"
 * Maklerbetreuer has to complete some checks in order for the "Antrag" (internally B/E)
 * from an external Makler to become a real Antrag.
 *
 * The "check" features are all in this component because offertstagetwo style classes
 * can be easily reused this way (see styleUrls)
 * The tradeoffs are potentially large files for this component.
 */
@Component({
  selector:        'app-offerteantraege-offene-makler-antraege',
  standalone:      true,
  templateUrl:     './offene-makler-antraege.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations:      [detailExpandAnimation],
  imports:         [
    AsyncPipe,
    FormatAdressPipe,
    FormatDatePipe,
    MatButton,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIconButton,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    NgTemplateOutlet,
    NoDataComponent,
    ReminderComponent,
    ThrottleEventDirective,
    MatSort,
    MatHeaderCellDef,
    MatIcon,
    MatTooltip,
    NgClass,
    MatPaginator,
    MatProgressSpinner
  ],
  styleUrls:       [
    './offene-makler-antraege.component.scss',
    '../offertstagetwo/offertstagetwo.component.scss',
    '../offertstagetwo/_offertstagetwo-theme.scss'
  ]
})
export class OffeneMaklerAntraegeComponent extends BasicPolizzeListComponent {

  // TODO description
  protected documentLayoutConfig: MaklerDocumentLayoutConfig[] = [
    {
      label:                        'Vollmachtsdokument',
      actionLabel:                  'Vollmacht',
      description:                  null,
      documentType:                 MaklerDocumentType.VOLLMACHT,
      getStatusTextFunction:        getVollmachtStatusText,
      getStatusTemplateRefFunction: this.getVollmachtStatusTemplateRef.bind(this)
    },
    {
      label:                        'Genehmigungsdokument',
      actionLabel:                  'Upload',
      description:                  null,
      documentType:                 MaklerDocumentType.GENEHMIGUNG,
      getStatusTextFunction:        getDocumentStatusText,
      getStatusTemplateRefFunction: this.getDocumentStatusIconTemplateRef.bind(this)
    }
  ];

  @ViewChild('check_true', {static: true}) checkTrue!: TemplateRef<void>;
  @ViewChild('check_false', {static: true}) checkFalse!: TemplateRef<void>;
  @ViewChild('check_undefined', {static: true}) checkUndefined!: TemplateRef<void>;

  get displayedColumns() {
    const columns = this.tableColumns.map(c => c.columnDef);
    columns.unshift('expand');
    columns.push('aktionen');
    columns.push('erstellen');
    return columns;
  }

  getAnzRowsPerEntry(): number {
    return 2;
  }

  protected antragAblehnen(polizze: PolizzeResult) {
    this.reactiveDialogService.open(RejectAntragDialogComponent, {
        width: '800px'
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef), take(1),
        switchMap(_dialogResult => {
          if (_dialogResult?.status === 'success') {
            this.llapi.patchValue('layout.spinner', true);
            return this.dataService.rejectMaklerPolizze(polizze.id, _dialogResult.data)
              .pipe(
                map(_payload => ({
                  _payload:             _payload,
                  _antragRejectRequest: _dialogResult.data
                }))
              );
          } else {
            /*
            this.messageService.showSnackBar({
              text: 'Aktion abgebrochen',
              type: MessageType.INFO
            });
             */
            return EMPTY;
          }
        })
      )
      .subscribe(({
                    _payload,
                    _antragRejectRequest
                  }) => {
        this.llapi.patchValue('layout.spinner', false);
        if (this.messageService.handlePayloadReturnSuccess(_payload)) {
          this.messageService.showSnackBar({
            text: 'Antrag abgelehnt',
            type: MessageType.INFO
          });
          this.removeFromList(polizze.id);
          if (_antragRejectRequest.changeType === AntragChangeType.CHANGE_BY_MAKLERBETREUER) {
            this.editRejectedMaklerAntrag(polizze);
          }
        }
      });
  }

  private editRejectedMaklerAntrag(polizze: PolizzeResult) {
    this.messageService.showSimpleYesNoDialog('Wollen Sie das Offert bearbeiten?')
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef), take(1),
        filter(_dialogResult => _dialogResult.status === 'yes')
      )
      .subscribe(
        _ => this.routerService.routeOffertStartPage(polizze.vke.id, polizze.id)
      );
  }

  /**
   * "Antrag freigeben" creates an Antrag from Beratung/Empfehlung
   * which is just called Antrag in the UI since Makler arent aware of the concept of Beratung/Empfehlung
   * https://issues.nv.at/ticketing/vpc/-/issues/2851
   *
   * TODO what happens after 3 months when i3j returns this error:
   * ZA-8989=Die Bearbeitung der Empfehlung ist nicht mehr mglich, da die Frist von :1: Monaten zur Speicherung des Antrages abgelaufen ist.
   * \nDie Beratung/Empfehlung bleibt erhalten und kann in den Offertstatus zurckgesetzt werden.
   *
   * VMC will clarify with maklerbetreuer stakeholders according to teams meeting 24.07.2025
   */
  protected antragFreigeben(polizze: PolizzeResult) {
    this.messageService.showSimpleYesNoDialog('Sind Sie sicher, dass Sie den Antrag freigeben wollen?')
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef), take(1),
        switchMap(_dialogResult => {
          if (_dialogResult.status === 'yes') {
            return this.dataService.savePolizzeAntrag(polizze.id);
          } else {
            return EMPTY;
          }
        }),
        filter(_payload => this.messageService.handlePayloadReturnSuccess(_payload))
      )
      .subscribe(() => {
        this.removeFromList(polizze.id);
        this.messageService.showSnackBar({
          text: 'Antrag freigegeben',
          type: MessageType.SUCCESS
        });
      });
  }

  // ============= VOLLMACHT STATUS =============

  protected getVollmachtStatusTemplateRef(details: MaklerPolizzeDetails) {
    const simpleVollmachtStatus = getSimpleVollmachtStatus(details);
    const checkOk = SIMPLE_VOLLMACHT_STATUS_CONFIG[simpleVollmachtStatus]?.checkOk;
    return this.getCheckTemplateRef(checkOk);
  }

  // ============= GENEHMIGUNG STATUS =============

  protected getDocumentStatusIconTemplateRef(details: MaklerPolizzeDetails): TemplateRef<void> {
    const documentType = MaklerDocumentType.GENEHMIGUNG; // vollmacht is now handled differently
    const documentExists = maklerDocumentExists(details, documentType);
    return this.getCheckTemplateRef(documentExists);
  }

  // ================================

  private getCheckTemplateRef(checkOk: boolean) {
    if (checkOk) {
      return this.checkTrue;
    } else {
      return this.checkFalse;
    }
  }
}
